import type { SelectProps } from '@byecode/ui'
import { Select, Text } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { ListItem4ByecodeUi, TEXT_FONT_NORMAL } from '@lighthouse/shared'
import React from 'react'

import { InheritLabel } from '../../InheritLabel'

const CN_FONTS = [
    'LXGWWenKai-Regular',
    'LXGWZhenKaiGB-Regular',
    'SourceHanSerifCN-Regular',
    'Zhuquefangsong',
    'BaoTuXiaoBaiTi-2',
    'EarlySummerSerif-Regular',
    'FangZhengKaiTiJianTi-1',
    'FangZhengShuSongJianTi-1',
    'GaoDuanHeiXiuDing151105-1',
    'GenShinGothic-Regular-2',
    'HeTianYanZhongWanP-3',
    'LianMengQiYiLuShuaiZhengRuiHeiTi-2',
    'Muyao-Softbrush-2',
    'PangMenZhengDaoBiaoTiTiMianFeiBan-2',
    'PangMenZhengDaoCuShuTi-2',
    'PangMenZhengDaoQingSongTi-2',
    'QianTuXiaoTuTi-2',
    'RuiZiZhenYanTiMianFeiShangYong-2',
    'ShouShuTi-2',
    'Slidefu-Regular-2',
    'MiSans-Regular',
    'XinYeNianTi-2',
    'YanShiChunFengKai-2',
    'YanShiQiuHongKai-2',
    'YanShiXiaXingKai-2',
    'YanShiYouRanXiaoKai-2',
    'YangRenDongZhuShiTi-2',
    'YaoSuiXinShouXieTi-2',
    'YouSheBiaoTiHei-2',
    'ZhanKuKuHei-1',
    'ZhanKuQingKeHuangYouTi-2',
    'ZhanKuWenYiTi-2',
    'ZhanKuXiaoLOGOTi-2',
    'ZhuoJianGanLanJianTi-Regular-2'
] as const

const EN_FONTS = [
    'Arial',
    'ABeeZee',
    'Abel',
    'AbhayaLibre',
    'AbrilFatface',
    'Acme',
    'Actor',
    'Adamina',
    'AdventPro',
    'AguafinaScript',
    'BadScript',
    'Bahiana',
    'Baloo',
    'BalooBhai',
    'BalooBhaijaan',
    'BalooBhaina',
    'BalooChettan',
    'BebasNeue',
    'Cabin',
    'CabinCondensed',
    'CabinSketch',
    'CaesarDressing',
    'Cagliostro',
    'Cairo',
    'Calligraffitti',
    'Cambay',
    'Cambo',
    'Cantarell',
    'CantataOne',
    'Capriola',
    'DIN-Alternate-Bold',
    'Damion',
    'DancingScript',
    'Dangrek',
    'DavidLibre',
    'Dawning-of-a-NewDay',
    'DaysOne',
    'Dekko',
    'Delius',
    'DeliusSwashCaps',
    'DeliusUnicase',
    'DellaRespira',
    'Devonshire',
    'Dhurjati',

    'JosefinSlab-VariableFont_wght',
    'NotoSans-VariableFont',
    'Playball-Regular',
    'PlayfairDisplay',
    'Poppins-SemiBold',
    'pp-formula',
    'PPEditorialNew',
    'Roboto-Medium',
    'TiltNeon',
    'Times-New-Roman'
] as const

const FONTS_NAME_MAP: Record<typeof CN_FONTS[number] | typeof EN_FONTS[number], string> = {
    Arial: 'Arial',
    ABeeZee: 'ABeeZee',
    Abel: 'Abel',
    AbhayaLibre: 'Abhaya Libre',
    AbrilFatface: 'Abril Fatface',
    Acme: 'Acme',
    Actor: 'Actor',
    Adamina: 'Adamina',
    AdventPro: 'Advent Pro',
    AguafinaScript: 'Aguafina Script',
    'BaoTuXiaoBaiTi-2': '包图小白体',
    BadScript: 'Bad Script',
    Bahiana: 'Bahiana',
    Baloo: 'Baloo',
    BalooBhai: 'Baloo Bhai',
    BalooBhaijaan: 'Baloo Bhaijaan',
    BalooBhaina: 'Baloo Bhaina',
    BalooChettan: 'Baloo Chettan',
    BebasNeue: 'BebasNeue',
    Cabin: 'Cabin',
    CabinCondensed: 'Cabin Condensed',
    CabinSketch: 'Cabin Sketch',
    CaesarDressing: 'Caesar Dressing',
    Cagliostro: 'Cagliostro',
    Cairo: 'Cairo',
    Calligraffitti: 'Calligraffitti',
    Cambay: 'Cambay',
    Cambo: 'Cambo',
    Cantarell: 'Cantarell',
    CantataOne: 'Cantata One',
    Capriola: 'Capriola',

    'DIN-Alternate-Bold': 'DIN Alternate',
    Damion: 'Damion',
    DancingScript: 'Dancing Script',
    Dangrek: 'Dangrek',
    DavidLibre: 'David Libre',
    'Dawning-of-a-NewDay': 'Dawning of a New Day',
    DaysOne: 'Days One',
    Dekko: 'Dekko',
    Delius: 'Delius',
    DeliusSwashCaps: 'Delius Swash Caps',
    DeliusUnicase: 'Delius Unicase',
    DellaRespira: 'Della Respira',
    Devonshire: 'Devonshire',
    Dhurjati: 'Dhurjati',

    'EarlySummerSerif-Regular': '初夏明朝體',
    'FangZhengKaiTiJianTi-1': '方正楷体简体',
    'LXGWWenKai-Regular': '霞鹜文楷',
    'LXGWZhenKaiGB-Regular': '霞鹜臻楷',
    'SourceHanSerifCN-Regular': '思源宋体',
    Zhuquefangsong: '朱雀仿宋',
    'FangZhengShuSongJianTi-1': '方正书宋简体',
    'GaoDuanHeiXiuDing151105-1': '站酷高端黑',
    'GenShinGothic-Regular-2': '思源真黑',
    'HeTianYanZhongWanP-3': '和田研字体',
    'JosefinSlab-VariableFont_wght': 'Josefin Slab',
    'LianMengQiYiLuShuaiZhengRuiHeiTi-2': '联盟起艺卢帅正锐黑体',
    'Muyao-Softbrush-2': '沐瑶软笔手写体',
    'NotoSans-VariableFont': 'Noto-Sans',
    'PangMenZhengDaoBiaoTiTiMianFeiBan-2': '庞门正道标题体',
    'PangMenZhengDaoCuShuTi-2': '庞门正道粗书体',
    'PangMenZhengDaoQingSongTi-2': '庞门正道轻松体',
    'Playball-Regular': 'Playball',
    PlayfairDisplay: 'Playfair Display',
    'Poppins-SemiBold': 'Poppins',
    'pp-formula': 'pp-formula',
    PPEditorialNew: 'PPEditorialNew',
    'QianTuXiaoTuTi-2': '千图小兔体',
    'Roboto-Medium': 'Roboto',
    'RuiZiZhenYanTiMianFeiShangYong-2': '锐字真言体',
    'ShouShuTi-2': '手书体',
    'Slidefu-Regular-2': '演示佛系体',
    TiltNeon: 'Tilt Neon',
    'Times-New-Roman': 'Times New Roman',
    'MiSans-Regular': '小米黑体',
    'XinYeNianTi-2': '新叶念体',
    'YanShiChunFengKai-2': '演示春风楷',
    'YanShiQiuHongKai-2': '演示秋鸿楷',
    'YanShiXiaXingKai-2': '演示夏行楷',
    'YanShiYouRanXiaoKai-2': '演示悠然小楷',
    'YangRenDongZhuShiTi-2': '杨任东竹石体',
    'YaoSuiXinShouXieTi-2': '沐瑶随心手写体',
    'YouSheBiaoTiHei-2': '优设标题黑',
    'ZhanKuKuHei-1': '站酷酷黑',
    'ZhanKuQingKeHuangYouTi-2': '站酷庆科黄油体',
    'ZhanKuWenYiTi-2': '站酷文艺体',
    'ZhanKuXiaoLOGOTi-2': '站酷小微LOGO体',
    'ZhuoJianGanLanJianTi-Regular-2': '卓键橄榄简体'
}
const FONT_FAMILY_OPTIONS = [
    {
        label: '默认',
        icon: <span>默认</span>,
        value: TEXT_FONT_NORMAL
    },
    {
        label: '中文',
        value: 'cn',
        children: CN_FONTS.map(font => {
            const icon = getAssetUrl('font', `${font}.svg`)
            return {
                label: FONTS_NAME_MAP[font],
                value: font,
                icon: <img width="100%" height="14" style={{ verticalAlign: 'middle' }} src={icon} alt={FONTS_NAME_MAP[font]} />
            }
        })
    },
    {
        label: '英文',
        value: 'en',
        children: EN_FONTS.map(font => {
            const icon = getAssetUrl('font', `${font}.svg`)
            return {
                label: FONTS_NAME_MAP[font],
                value: font,
                icon: <img width="100%" height="14" style={{ verticalAlign: 'middle' }} src={icon} alt={FONTS_NAME_MAP[font]} />
            }
        })
    }
]
interface FontFamilyControlProps {
    notForm?: boolean
    value?: string
    fontFamilyInputProps?: SelectProps
    name?: string
    onChange?: (v: string) => void
    onChangeEnd?: (v: string) => void
}
export const FontFamilyControl: React.FunctionComponent<FontFamilyControlProps> = ({
    notForm,
    value,
    name,
    fontFamilyInputProps,
    onChange,
    onChangeEnd
}) => {
    return (
        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
            {notForm ? <Text>字体</Text> : name && <InheritLabel label="字体" name={name} />}
            <Select
                value={value}
                onChange={v => {
                    onChange?.(v)
                    onChangeEnd?.(v)
                }}
                searchable
                options={FONT_FAMILY_OPTIONS}
                prefix={false}
                position="left-start"
                styles={{ root: { width: 180 }, item: { padding: '9px 16px' }, label: { display: 'none' } }}
                {...fontFamilyInputProps}
            />
        </ListItem4ByecodeUi>
    )
}
